import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { trpc } from "app/trpc"
import { Value, htmlEmptyTags, htmlFormatTags } from "@stringtale/react"
import { useCurrentGroup } from "app/core/hooks/useCurrentGroup"
import { useRouter } from "app/core/hooks/useRouter"
import { useEffect, useState } from "react"
import { PX14, PX32 } from "theme/sizes"
import Container from "ui/Container"
import Group from "ui/Group"
import Hr from "ui/Hr"
import RoundedButton from "ui/RoundedButton"
import Stack from "ui/Stack"
import Text from "ui/Text"
import TextLink from "ui/TextLink"
import Title from "ui/Title"
import Layout from "../Layout"
import { useCurrentYear } from "../hooks/useCurrentYear"
import Loader from "ui/Loader"

export default function MigrateGroupComponent({
  onComplete,
}: {
  onComplete: () => void
}) {
  const currentYear = useCurrentYear()
  const migrateGroupMutation = useMutation(
    trpc.schooljaren.migrateGroup.mutationOptions()
  )
  const migrateGroupMutationRes = {
    isPending: migrateGroupMutation.isPending,
    isSuccess: false,
  }
  const doNotMigrateGroupMutation = useMutation(
    trpc.schooljaren.doNotMigrateGroup.mutationOptions()
  )
  const doNotMigrateGroupMutationRes = {
    isPending: doNotMigrateGroupMutation.isPending,
    isSuccess: false,
  }

  const { data: originGroup } = useSuspenseQuery(
    trpc.schooljaren.getMigrationGroup.queryOptions()
  )
  const { data: currentGroup } = useSuspenseQuery(
    trpc.users.getCurrentGroup.queryOptions()
  )
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
    useState(false)

  // Protect against multiple migrations
  useEffect(() => {
    if (
      currentGroup?.migrationState === "MIGRATED" ||
      currentGroup?.migrationState === "NOT_MIGRATED"
    ) {
      router.push("/")
    }
  }, [currentGroup, router])

  if (currentGroup?.migrationState !== "INIT") {
    return <div></div>
  }

  return (
    <Layout
      blueprint={currentYear}
      title="Kopieer groepslijsten"
      hideCloseButton
    >
      <Container width="596px">
        {/*
          When you still have to pick wether to 'keep' the migrated lists or not, show the decisions state
          When you've picked not to migrate, confirm that they've been emptied. In reality they were never
          migrated to start with, the migration happens when you pick migrate, but we market things differently.
        */}
        {!doNotMigrateGroupMutationRes.isSuccess && (
          <Stack gap="16px">
            <Title size={PX32} weight="900">
              <Value name="apps.web.src.schooljaren.components.migrategroupcomponent.title">
                De groepslijsten zijn overgezet naar het nieuwe schooljaar
              </Value>
            </Title>
            <Hr />
            <Text size={PX14}>
              {originGroup.displayTitle.toLowerCase() ===
              currentGroup?.displayTitle.toLowerCase() ? (
                <Value
                  name="apps.web.src.schooljaren.components.migrategroupcomponent.body"
                  format={{
                    ...htmlEmptyTags,
                    currentgroup: <b>{currentGroup?.displayTitle}</b>,
                  }}
                >
                  {`We hebben alle groepslijsten van {currentgroup} overgezet naar het huidige schooljaar.
                Je vindt al je lijsten terug in de bibliotheek.{br}{br}
                Wil je liever met een schone lei beginnen? Klik dan
                op ‘Groepslijsten niet overzetten’. Je kunt oude groepslijsten
                altijd terugvinden in het groepsarchief in de bibliotheek.`}
                </Value>
              ) : (
                <Value
                  name="apps.web.src.schooljaren.components.migrategroupcomponent.body"
                  format={{
                    ...htmlEmptyTags,

                    originGroup: `${originGroup.displayTitle}${
                      originGroup.archiveYear
                        ? ` (${originGroup.archiveYear.displayTitle})`
                        : ""
                    }`,
                    currentgroup: currentGroup?.displayTitle,
                  }}
                >
                  {`We hebben alle groepslijsten van {originGroup} overgezet naar {currentgroup} in het
                huidige schooljaar. Je vindt al je lijsten terug in de
                bibliotheek.{br}{br}Wil je liever met een schone lei beginnen? Klik dan
                op ‘Groepslijsten niet overzetten’. Je kunt oude groepslijsten
                altijd terugvinden in het groepsarchief in de bibliotheek.`}
                </Value>
              )}
            </Text>
            <Group align="center" justify="space-between">
              <TextLink
                bold
                onClick={async () => {
                  await doNotMigrateGroupMutation.mutateAsync()
                  queryClient.removeQueries({
                    queryKey: trpc.users.getCurrentGroup.queryKey(),
                  })
                  onComplete()
                }}
              >
                <Value name="apps.web.src.schooljaren.components.migrategroupcomponent.ik_wil_alles_weggooien_en_opnieuw_starten">
                  Groepslijsten niet overzetten
                </Value>
              </TextLink>
              <RoundedButton
                type="button"
                variant="filled"
                onClick={async () => {
                  await migrateGroupMutation.mutateAsync({
                    groupId: currentGroup.id,
                  })
                  queryClient.removeQueries({
                    queryKey: trpc.users.getCurrentGroup.queryKey(),
                  })
                  queryClient.removeQueries({
                    queryKey: trpc.auth.getGroupsInSchool.queryKey(),
                  })
                  onComplete()
                }}
                disabled={
                  migrateGroupMutationRes.isPending ||
                  doNotMigrateGroupMutationRes.isPending
                }
              >
                {migrateGroupMutationRes.isPending ? (
                  <Loader color="#FFFFFF" />
                ) : (
                  <Value name="apps.web.src.schooljaren.components.migrategroupcomponent.verder">
                    Verder
                  </Value>
                )}
              </RoundedButton>
            </Group>
          </Stack>
        )}
        {doNotMigrateGroupMutationRes.isSuccess && (
          <Stack gap="16px">
            <Title size={PX32} weight="900">
              <Value name="apps.web.src.schooljaren.components.migrategroupcomponent.veel_plezier_in_het_nieuwe_schooljaar">
                Veel plezier in het nieuwe schooljaar!
              </Value>
            </Title>
            <Hr />
            <Text size={PX14}>
              <Value
                name="apps.web.src.schooljaren.components.migrategroupcomponent.not_migrated"
                format={{
                  groupName: currentGroup?.displayTitle,
                  ...htmlFormatTags,
                }}
              >
                {
                  /* HTML */ `De groepslijsten van <b>{groupName}</b> zijn leeg,
                    zodat je met een schone lei aan het nieuwe schooljaar kunt
                    beginnen. Wil je oude afspeellijsten toch nog terugzien? Dat
                    kan in het archief.`
                }
              </Value>
            </Text>
            <Group align="center" justify="flex-end">
              <RoundedButton
                variant="filled"
                type="button"
                disabled={isContinueButtonDisabled}
                onClick={async () => {
                  onComplete()
                  setIsContinueButtonDisabled(true)
                }}
              >
                {isContinueButtonDisabled ? (
                  <Loader color="#FFFFFF" />
                ) : (
                  <Value name="apps.web.src.schooljaren.components.migrategroupcomponent.verder">
                    Verder
                  </Value>
                )}
              </RoundedButton>
            </Group>
          </Stack>
        )}
      </Container>
    </Layout>
  )
}
